import { useEffect, useState, useCallback } from "react";
import { Download, Smartphone, X, Apple, Share } from "lucide-react";

/**
 * PWA install prompt + iOS instructions.
 *
 * On Chromium / Edge / Samsung Internet, the browser fires `beforeinstallprompt`
 * which we capture and surface as a friendly bottom-sheet. On iOS Safari the
 * event never fires, so we sniff the UA and show step-by-step "Share → Add to
 * Home Screen" instructions instead.
 *
 * Suppression: if the user dismisses, we store a 30-day cool-down in
 * localStorage so we never nag. If the app is already running as a PWA
 * (`display-mode: standalone`) we never show the prompt at all.
 */
const STORAGE_KEY = "crc_pwa_prompt_dismissed_at";
const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const FIRST_SHOW_DELAY_MS = 6000; // give the page time to settle

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("android"); // "android" | "ios"

  const dismissed = useCallback(() => {
    try {
      const v = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      return v && Date.now() - v < COOLDOWN_MS;
    } catch (_) { return false; }
  }, []);

  const isStandalone = useCallback(() => {
    return (
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  }, []);

  useEffect(() => {
    if (isStandalone() || dismissed()) return;

    // Chromium / Edge / Samsung Internet — capture the event and surface UI.
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setMode("android");
      setTimeout(() => setOpen(true), FIRST_SHOW_DELAY_MS);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari — no install event. Show our own help sheet on first visit.
    const ua = navigator.userAgent || "";
    const isIOS = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
    if (isIOS) {
      setMode("ios");
      const t = setTimeout(() => setOpen(true), FIRST_SHOW_DELAY_MS);
      return () => { window.removeEventListener("beforeinstallprompt", onPrompt); clearTimeout(t); };
    }

    // Hide when actually installed.
    const onInstalled = () => setOpen(false);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [dismissed, isStandalone]);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (_) { /* noop */ }
    setOpen(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (_) { /* noop */ }
    setDeferred(null);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden animate-[slideUp_.3s_ease-out]"
      style={{ animationName: "slideUp" }}
    >
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center">
            {mode === "ios" ? <Apple className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
          </div>
          <div className="font-bold text-sm">Install Central Rentals</div>
        </div>
        <button
          data-testid="pwa-install-close"
          onClick={dismiss}
          className="p-1.5 rounded-md hover:bg-white/15"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5">
        {mode === "ios" ? (
          <>
            <p className="text-sm text-slate-700 leading-relaxed">
              Add Central Rentals to your iPhone home screen — opens fullscreen, no Safari bar.
            </p>
            <ol className="mt-3 space-y-2 text-sm text-slate-700">
              <li className="flex items-start gap-2">
                <span className="flex-none w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold flex items-center justify-center">1</span>
                <span>Tap <Share className="inline w-3.5 h-3.5 -mt-0.5" /> <strong>Share</strong> at the bottom of Safari.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-none w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold flex items-center justify-center">2</span>
                <span>Scroll and tap <strong>Add to Home Screen</strong>.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-none w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold flex items-center justify-center">3</span>
                <span>Tap <strong>Add</strong> — the Central Rentals icon will land on your home screen.</span>
              </li>
            </ol>
            <button
              data-testid="pwa-install-ios-ok"
              onClick={dismiss}
              className="mt-4 w-full px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700"
            >
              Got it
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-700 leading-relaxed">
              Install Central Rentals on your phone — opens like a native app, with one-tap access to rent, leases, and the Smart Operator.
            </p>
            <div className="mt-4 flex items-center gap-2">
              <button
                data-testid="pwa-install-cta"
                onClick={install}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700"
              >
                <Download className="w-4 h-4" /> Install app
              </button>
              <button
                data-testid="pwa-install-not-now"
                onClick={dismiss}
                className="px-4 py-2.5 rounded-lg text-slate-600 text-sm font-semibold hover:bg-slate-50"
              >
                Not now
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`@keyframes slideUp { from { transform: translate(-50%, 30px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }`}</style>
    </div>
  );
}
